import { formatBrowseResultCount } from '@uniquedomains/components/radar/catalogs/browseResultCountCopy';
import type { DatabaseBrowseCountPrecision } from '@uniquedomains/components/contracts/domains/browseFacets';

export const formatCompactStat = (value: number): string => {
    const safeValue = Number.isFinite(value) ? value : 0;

    return new Intl.NumberFormat('en-US', {
        maximumSignificantDigits: 3,
        notation: 'compact',
    }).format(safeValue);
};

/**
 * The public tables' "of N" count: null unless the stat is a positive number, because a footer
 * reading "of 0" would describe an outage as an empty catalog.
 *
 * A catalog-wide search's count is a page-sample estimate (ADR-0034 amendment of 2026-10-12), so
 * it goes through the one estimate formatter: "~7,370,000", never "7,368,450". Absent means exact.
 */
export const formatPositiveCountStat = (
    value: number | null | undefined,
    countPrecision: DatabaseBrowseCountPrecision = 'exact',
): string | null =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? formatBrowseResultCount(value, countPrecision) : null;

/**
 * Spells the magnitude out ("7.4 million") for prose headlines, where the compact
 * "7.37M" form reads like a spec sheet.
 *
 * Returns null below one million and when the stat is missing, because "0.7 million"
 * and "0 million" are worse than no number at all. Callers must supply a wording that
 * works without a count; the stats endpoint returns 0 whenever it is unreachable.
 */
export const formatMillionsStat = (value: number): string | null => {
    if (!Number.isFinite(value) || value < 1_000_000) return null;

    const millions = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value / 1_000_000);

    return `${millions} million`;
};
